// src/components/NewsTicker.js
import React, { useState, useEffect, useRef } from "react";
import styles from "./NewsTicker.module.css";

export default function NewsTicker() {
  const [headlines, setHeadlines] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const prevXRef = useRef(0);
  const prevTimeRef = useRef(0);
  const velocityRef = useRef(0);

  const isPausedRef = useRef(false);
  const pauseTimeoutRef = useRef(null);

  // fetch RSS headlines once
  useEffect(() => {
    const rssUrl = "https://www.afl.com.au/rss/";
    const fetchVia = (proxy) =>
      fetch(proxy).then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      });
    const primary = `https://corsproxy.io/?${rssUrl}`;
    const fallback = `https://api.allorigins.win/raw?url=${rssUrl}`;

    fetchVia(primary)
      .catch(() => fetchVia(fallback))
      .then((xmlText) => {
        const xml = new DOMParser().parseFromString(xmlText, "text/xml");
        const items = Array.from(xml.querySelectorAll("item"))
          .slice(0, 10)
          .map((item) => ({
            title: item.querySelector("title")?.textContent || "",
            link: item.querySelector("link")?.textContent || "#",
          }));
        setHeadlines(items);
      })
      .catch(() => {
        setHeadlines([{ title: "News unavailable", link: "#" }]);
      });
  }, []);

  // auto‑scroll loop with pause
  useEffect(() => {
    let raf;
    let lastTime = null;
    let scrollBuffer = 0;

    const step = (now) => {
      const ctr = containerRef.current;
      if (!ctr) return;

      if (!lastTime) lastTime = now;
      const elapsed = now - lastTime;
      lastTime = now;

      if (!isDragging && !isPausedRef.current) {
        const pxPerSecond = 100;
        const delta = (pxPerSecond * elapsed) / 1000;
        scrollBuffer += delta;

        if (scrollBuffer >= 1) {
          const move = Math.floor(scrollBuffer);
          scrollBuffer -= move;

          const half = ctr.scrollWidth / 2;
          let next = ctr.scrollLeft + move;
          if (next >= half) next -= half;
          ctr.scrollLeft = next;
        }
      }

      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [isDragging]);

  // pointer handlers with velocity
  const onPointerDown = (e) => {
    clearTimeout(pauseTimeoutRef.current);
    setIsDragging(true);
    startXRef.current = e.clientX - containerRef.current.offsetLeft;
    scrollLeftRef.current = containerRef.current.scrollLeft;

    prevXRef.current = e.clientX;
    prevTimeRef.current = performance.now();
    velocityRef.current = 0;
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    const x = e.clientX - containerRef.current.offsetLeft;
    const walk = x - startXRef.current;
    containerRef.current.scrollLeft = scrollLeftRef.current - walk;

    const now = performance.now();
    const dx = e.clientX - prevXRef.current;
    const dt = now - prevTimeRef.current;
    velocityRef.current = dx / dt;
    prevXRef.current = e.clientX;
    prevTimeRef.current = now;
  };

  const onPointerUp = () => {
    setIsDragging(false);
    isPausedRef.current = true;
    clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 2000);

    // momentum fling
    let v = velocityRef.current * 20; // adjust multiplier as needed
    const fling = () => {
      if (Math.abs(v) < 0.1) return;
      containerRef.current.scrollLeft -= v;
      v *= 0.95; // friction
      requestAnimationFrame(fling);
    };
    requestAnimationFrame(fling);
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.tickerContainer} ${
        isDragging ? styles.dragging : ""
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className={styles.tickerContent}>
        {headlines.concat(headlines).map((h, i) => (
          <a key={i} href={h.link} target="_blank" rel="noopener noreferrer">
            {h.title}
          </a>
        ))}
      </div>
    </div>
  );
}
